/**
 * C1 v1.15 (c1-strategies-editor, companion of precision-pricing c2-strategy-editing, C2 v1.17): the Strategies tab's reads
 * and writes and every sentence of the tab. Types mirror C2's JSON field for field; a threshold or target is the API's string
 * and a count C2's integer, only ever shown, never computed with (H12). The fields C2 v1.17 adds are optional until
 * `deploy/api.md` step 20 is live, so a v1.16 answer still types. C1 v1.7 built the read-only map and cards; the editor keeps
 * those reads and adds one write function per C2 route, the fold that drives the map's dropdowns and the pure helpers below.
 */
import type { QueryClient } from "@tanstack/react-query";

import { call, type Basis, DEFAULT_BASIS, type ErrorFacts, GRID_STALE_TIME, PricingError, type Channel, type Market, type ProposeEnding, type UnverifiedRule } from "./api";
import { writeRefusal } from "./config";
import { channelLabel, MARKET_LABEL, money, STORE_LABEL } from "./format";
import { PROJECTION_KEY } from "./projection";

export type MetricUnit = "pts" | "money";
/** The three lenses a metric belongs to (C2 v1.17 `lens`), as the drawer heads them. */
export type Lens = "productivity" | "customer" | "competition";
/** One vocabulary row. C2 v1.17 adds the metric's lens and `no_data`: why the evaluator never judges it, null when it does. */
export type Metric = { metric: string; label: string; unit: MetricUnit; definition: string; lens?: Lens; no_data?: string | null };
export type Operator = ">=" | "<=" | ">" | "<" | "=" | "~";
export type ChannelScope = "any" | "amazon" | "shopify";
/** An active rule. C2 v1.17 adds the rule template it links (the store finds or creates it on every rule write) and C2's sentence. */
export type Rule = {
  id: number; priority: number; metric: string; operator: Operator; threshold: string; channel_scope: ChannelScope;
  set_by: string; set_at: string; template_id?: number | null; text?: string;
};
export type Goal = { metric: string; target: string };
/** One active strategy as C2 serves it: a goal or the not-set sentence, its active rules by priority, how many rows use it.
 * C2 v1.17 adds the last edit (the latest of the strategy row and its active rules), which the card stamps. */
export type StrategyEntry = {
  id: number; name: string; goal: Goal | null; goal_not_set: string | null; rules: Rule[]; assignment_count: number;
  assigned_retired_skus: string[]; source: string; set_by: string; set_at: string; edited_by?: string; edited_at?: string;
};
export type StrategyRef = { id: number; name: string };
/** A rule template (C2 v1.17): the exact (metric, operator, threshold, channel scope) tuple, the strategies whose rules link it,
 * and C2's sentence. Served in C2's order: lens, label, operator, threshold. */
export type Template = {
  id: number; metric: string; label: string; lens: Lens; operator: Operator; threshold: string; channel_scope: ChannelScope;
  text: string; no_data: string | null; used_by: StrategyRef[]; used_by_count: number; set_by: string; set_at: string;
};
export type Library = { metrics: Metric[]; strategies: StrategyEntry[]; strategy_count: number; templates?: Template[] };

export type Grain = "lane" | "marketplace" | "store";
export type Assignment = { strategy_id: number; strategy_name: string; note: string | null; source: string; set_by: string; set_at: string };
export type Effective = { strategy_id: number; strategy_name: string; marketplace_id: string };
/** A product note (per tracking and variant) or a lane note (per channel location and marketplace), C2 v1.17. */
export type Note = { text: string; set_by: string; set_at: string };
/** One map row at the assignment grain: an Amazon SKU's lane (ALL) then its marketplaces; a Shopify SKU's store row (ALL).
 * C2 v1.17 adds the product note, the lane note (on marketplace rows, and on the lane row of a one-marketplace pool), whether
 * the row carries an own marketplace assignment (`override`) and whether its pool has one marketplace (`pool_single`). */
export type MapRow = {
  xero_sku: string; marketplace_id: string; marketplace_name: string | null; grain: Grain; product: string; variant: string;
  channel: Channel; channel_location: string; market: Market; units_per_case: number | null; assignment: Assignment | null;
  effective: Effective | null; inherited: boolean | null; not_set: string | null;
  product_note?: Note | null; lane_note?: Note | null; override?: boolean; pool_single?: boolean;
};
export type StrategyMap = {
  market: Market; strategised: boolean; not_strategised: string | null; row_count: number; assigned_count: number; rows: MapRow[];
};
/** One row a strategy is assigned on (C2 v1.17 `GET /api/strategies/{id}/cells`), for the delete guard; a retired SKU's row too. */
export type StrategyCell = {
  xero_sku: string; marketplace_id: string; marketplace_name: string | null; product: string; variant: string; channel: Channel;
  market: Market; sku_active: boolean;
};

export const MARKETS: Market[] = ["UK", "EU", "AE"];
export const OPERATOR_WORD: Record<Operator, string> = {
  ">=": "at least", "<=": "at most", ">": "more than", "<": "less than", "=": "exactly", "~": "as close as possible to",
};
export const SCOPE_WORD: Record<ChannelScope, string | null> = { any: null, amazon: "If channel = Amazon, ", shopify: "If channel = Shopify, " };

/** Every sentence of the tab. The editor's words are Prototype v26's (index.html 3647-3871), adapted where the store differs:
 * a delete may leave cells with no strategy, the map edits by dropdown, notes live on the map. The editor retired C1 v1.7's
 * read-only sentence and its "No rules yet." row; the no-SKU mark is a plain dash (v26), not the module's em-dash `ABSENT`. */
export const STRATEGIES_COPY = {
  title: "Strategies",
  desc: "Named pricing strategies (prioritised rules over the lenses) and the product-channel map assigning one to every SKU and channel.",
  newStrategy: "New strategy",
  retry: "Retry",
  // the inline alert over a failed refetch that kept the page's reads (with nothing to show, the full-page alert says `messageFor`)
  notRefreshed: "The strategies could not be refreshed, so this page may be out of date. Retry before changing anything.",
  map: {
    title: "Product-channel strategy map", columns: { product: "Product" }, none: "-",
    // a marketplace set apart, under its lane's dropdown; its lane note shows under Marketplace notes, not here (review round 2)
    override: (marketplace: string, strategy: string) => `${marketplace}: ${strategy}`,
    noStrategy: "No strategy", sameAsLane: (lane: string) => `Same as ${lane}`,
    cellLabel: (product: string, place: string) => `Strategy for ${product} on ${place}`,
    marketplaces: "Marketplaces", hideMarketplaces: "Hide marketplaces", marketplaceNotes: "Marketplace notes", hideMarketplaceNotes: "Hide marketplace notes",
    productNote: "Product note", laneNote: (place: string) => `Note for ${place}`,
    // the assignment's own note, under the cell's dropdown (and under a marketplace set apart)
    cellNote: (product: string, place: string) => `Note for ${product} on ${place}`,
    addNote: "Add note", editNote: "Edit note", saveNote: "Save note", clearNote: "Clear note", cancel: "Cancel",
    noteAction: (action: string, subject: string) => `${action}: ${subject}`,
    notePlaceholder: "What should the next person pricing this know?",
    noteBy: (by: string, at: string) => `${by} · ${at}`,
  },
  library: {
    title: "Rule library", create: "Create new rule", columns: ["Rule", "Used by", ""],
    usedBy: (n: number) => (n === 0 ? "not used" : n === 1 ? "1 strategy" : `${n} strategies`),
    delete: "Delete", deleteLabel: (template: string) => `Delete ${template}`,
    inUseTitle: "Cannot delete this rule", ok: "OK", thisRule: "This rule",
    inUse: (template: string, names: string[]) =>
      `${template} is used by ${names.join(", ")}. Remove it from ${names.length === 1 ? "that strategy" : "those strategies"} first.`,
    empty: "No rule templates yet. Create one to reuse it in any strategy.",
    unavailable: "The pricing API serving this page lists no rule templates.",
    noData: (reason: string) => `not checked yet: ${reason}`,
  },
  builder: {
    addTitle: (strategy: string) => `Add rule to ${strategy}`,
    editTitle: (priority: number, strategy: string) => `Edit rule ${priority} of ${strategy}`,
    templateTitle: "Create new rule (saves to the rule library)",
    templateTitleFor: (strategy: string) => `Create new rule (saves to the rule library and adds it to ${strategy})`,
    fromLibrary: "Rule from the library", pickTemplate: "Choose from the rule library",
    metric: "Metric", pickMetric: "Choose a metric", operator: "Operator", threshold: "Threshold", condition: "Condition",
    saveRule: "Save rule", saveToLibrary: "Save to library", cancel: "Cancel", createNew: "Create new rule",
    metricRequired: "Choose a metric.",
    thresholdRequired: "Enter a threshold number, with at most four decimals.",
  },
  create: {
    title: "New strategy", name: "Name", namePlaceholder: "e.g. Launch Defend 10%", goal: "Optimisation goal", asCloseTo: "as close to", target: "Goal target",
    targetPlaceholder: "35", submit: "Create strategy", cancel: "Cancel",
    nameRequired: "Name the strategy.", nameTaken: "A strategy with that name exists.", goalRequired: "Choose the optimisation goal.",
    targetRequired: "Enter a goal target number, with at most four decimals.",
  },
  definitions: {
    title: "Strategy definitions", empty: "No strategies defined yet.", emptyHint: "Define a strategy to drive the Price Run rules.",
    columns: ["Priority", "Rule"], actions: "Actions", goal: "Goal", assigned: (n: number) => `${n} assigned`,
    onRetired: (skus: string[]) => (skus.length === 1 ? `1 on a retired SKU: ${skus[0]}` : `${skus.length} on retired SKUs: ${skus.join(", ")}`),
    setBy: (by: string, at: string, source: string) => (source === "legacy_sheet" ? `Imported from the legacy sheet by ${by} · ${at}` : `Set by ${by} · ${at}`),
    footnote: "Edits save straight away and change no live price, so there is no Commit step. Price Run re-checks every assigned row on its next render.",
    noRulesAdd: "No rules yet. Add one from the rule library.",
    goalNote: "(optimisation goal)",
    editedBy: (by: string, at: string) => `Edited by ${by} on ${at}`,
    addRule: "Add rule", deleteStrategy: "Delete strategy", rename: "Rename", editGoal: "Edit goal", save: "Save", cancel: "Cancel",
    forStrategy: (action: string, strategy: string) => `${action}, ${strategy}`,
    up: "Up", down: "Down", moveUp: "Move up", moveDown: "Move down", edit: "Edit", delete: "Delete",
    editRule: (priority: number) => `Edit rule ${priority}`, deleteRule: (priority: number) => `Delete rule ${priority}`,
  },
  remove: {
    title: (name: string) => `Delete ${name}?`,
    unassigned: "It is not assigned to any product and channel. This removes it and its rules.",
    // n is C2's count on active SKUs (design call G); the list under it also names each row on a retired SKU, so the sentence counts those too
    assigned: (name: string, n: number, retired = 0) =>
      `${name} is assigned to ${n} product and channel ${n === 1 ? "cell" : "cells"}${retired
        ? `, plus ${retired} on ${retired === 1 ? "a retired SKU" : "retired SKUs"} that ${retired === 1 ? "is" : "are"} cleared on delete` : ""}. Choose a replacement, or No strategy, before deleting.`,
    retiredCell: "retired SKU, cleared on delete",
    replacement: "Replacement strategy", choose: "Choose a replacement", noStrategy: "No strategy",
    replaceAndDelete: "Replace and delete", confirm: "Delete strategy", cancel: "Cancel",
    chooseFirst: "Choose a replacement strategy first.",
    chooseAgain: "The replacement you chose was changed or deleted elsewhere. Choose a replacement again.",
    reading: "Reading the cells it is assigned to…",
    unread: "The cells it is assigned to could not be read, so it cannot be deleted yet. Try again shortly.",
  },
  done: {
    created: (name: string) => `Created ${name}. Add rules from the rule library.`,
    renamed: (from: string, to: string) => `Renamed ${from} to ${to}.`,
    goal: (name: string) => `Saved the goal of ${name}.`,
    // C2's counts: `unassigned` cells are left with no strategy; `follows_lane` marketplace overrides now read their lane's
    // strategy (No strategy on an override); rows on retired SKUs (`retired_cleared`) are not cells of the map, so they go unsaid
    deleted: (name: string, answer: Pick<RetireAnswer, "replacement" | "reassigned" | "unassigned" | "follows_lane">) => {
      const moved = answer.replacement && answer.reassigned
        ? ` ${answer.reassigned} ${answer.reassigned === 1 ? "cell now uses" : "cells now use"} ${answer.replacement.name}.` : "";
      const cleared = !answer.replacement && answer.unassigned
        ? ` ${answer.unassigned} ${answer.unassigned === 1 ? "cell now has" : "cells now have"} no strategy.` : "";
      const follows = !answer.replacement && answer.follows_lane
        ? ` ${answer.follows_lane} ${answer.follows_lane === 1 ? "marketplace cell now follows its lane" : "marketplace cells now follow their lanes"}.` : "";
      return `Deleted ${name}.${moved}${cleared}${follows}`;
    },
    ruleAdded: (priority: number, strategy: string) => `Added rule ${priority} to ${strategy}.`,
    ruleSaved: (priority: number, strategy: string) => `Saved rule ${priority} of ${strategy}.`,
    ruleDeleted: (priority: number, sentence: string, strategy: string) => `Deleted rule ${priority} of ${strategy} (${sentence}). The rules after it moved up.`,
    reordered: (strategy: string) => `Saved the new rule order of ${strategy}.`,
    templateSaved: (template: string, strategy?: string | null) =>
      `Saved ${template} to the rule library.${strategy ? ` Added to ${strategy}.` : ""}`,
    templateDeleted: (template: string) => `Deleted ${template} from the rule library.`,
    assigned: (product: string, place: string, strategy: string) => `${product} on ${place} now uses ${strategy}.`,
    cleared: (product: string, place: string) => `${product} on ${place} now has no strategy.`,
    // a cleared lane whose marketplaces keep their own rows (C2 retires only the lane's): those still run, so the line names them
    clearedKeeps: (product: string, place: string, kept: string, many = false) =>
      `${product} on ${place} now has no strategy for the whole channel. ${kept} ${many ? "keep their own" : "keeps its own"}.`,
    followsLane: (product: string, place: string) => `${product} on ${place} now follows the lane.`,
    noteSaved: "Note saved.", noteCleared: "Note cleared.",
    unchanged: "No change: that was already saved.",
  },
  /** The sentences for a refused write that are the tab's own, by C2 reason; every other refusal is C2's sentence (`strategyRefusal`).
   * A stale rule id comes back as `changed_elsewhere` (design call D); only the priority routes the editor does not call say `unknown_rule`.
   * A note's `bad_note` is C2's own: the textarea cannot send more than `NOTE_MAX` characters, so the refusal a person can reach is a
   * pasted NUL character, which C2's sentence names. */
  refusal: {
    unreachable: "The pricing API did not answer, so nothing was saved. Try again shortly.",
    noEdit: (areaName: string) => `You can view strategies here. Ask the ${areaName} area owner for edit access to change them.`,
    notLive: "The pricing API does not take this change yet, so nothing was saved.",
    byReason: {
      strategy_exists: "A strategy with that name exists.",
      changed_elsewhere: "Someone changed this at the same time, so nothing was saved. The page now shows the latest: check it and try again.",
      unknown_strategy: "That strategy was changed or deleted elsewhere, so nothing was saved. The page now shows the latest.",
      unknown_template: "That library rule was deleted elsewhere, so nothing was saved. The page now shows the latest.",
      unknown_assignment: "That cell was already cleared elsewhere. The page now shows the latest.",
      same_strategy: "Choose a different strategy as the replacement.",
      single_marketplace_pool: "This lane sells on one marketplace, so its strategy is set for the whole lane.",
    } as Record<string, string>,
  },
  metrics: { title: "Metric definitions", desc: "The metrics a rule or a goal may name, in the store's words. Percentages are in points (35 = 35%).", columns: ["Metric", "Unit", "Definition"] },
  unit: { pts: "% points", money: "money" } as Record<MetricUnit, string>,
  lens: { productivity: "Productivity lens", customer: "Customer lens", competition: "Competition lens", other: "Other metrics" },
  // the builder's word for an operator is the one C2's rule sentence and `ruleLabel` use, so a saved rule reads as it was picked
  operator: OPERATOR_WORD,
  scope: { any: "Any channel", amazon: "If channel = Amazon", shopify: "If channel = Shopify" } as Record<ChannelScope, string>,
};

// Reads and writes go through the module's one `call` (api.ts): the cookie rides on credentials, no-store so a save shows on
// the next read, and a non-2xx throws `PricingError` with the parsed error body.
export const getLibrary = () => call<Library>("/strategies");
export const getStrategyMap = (market: Market) => call<StrategyMap>(`/strategies/map/${market}`);

export const LIBRARY_KEY = ["pricing", "strategies"] as const;
export const MAP_KEY = (market: Market) => ["pricing", "strategies", "map", market] as const;
/** Reused for the grid's stale window: a strategy changes when a person saves it, and the tab refetches on focus. */
export function libraryQuery() {
  return { queryKey: LIBRARY_KEY, queryFn: getLibrary, retry: 1, staleTime: GRID_STALE_TIME };
}
export function mapQuery(market: Market) {
  return { queryKey: MAP_KEY(market), queryFn: () => getStrategyMap(market), retry: 1, staleTime: GRID_STALE_TIME };
}

/** The metric's label from the store's vocabulary; the key itself when the vocabulary does not carry it. */
export function metricLabel(metrics: Metric[], key: string): string {
  return metrics.find((m) => m.metric === key)?.label ?? key;
}
function unitSuffix(metrics: Metric[], key: string): string {
  return metrics.find((m) => m.metric === key)?.unit === "money" ? "" : "%";
}
/** "If channel = Shopify, Channel Consistency must be at most -10%": the Canvas v25 rule sentence from C2's fields. */
export function ruleLabel(rule: Rule, metrics: Metric[]): string {
  return `${SCOPE_WORD[rule.channel_scope] ?? ""}${metricLabel(metrics, rule.metric)} must be ${OPERATOR_WORD[rule.operator]} ${rule.threshold}${unitSuffix(metrics, rule.metric)}`;
}
/** "Get Fulfilment Margin as close to 35% as possible" (the optimisation goal), or the API's not-set sentence. */
export function goalLabel(entry: StrategyEntry, metrics: Metric[]): string {
  if (!entry.goal) return entry.goal_not_set ?? "";
  return `Get ${metricLabel(metrics, entry.goal.metric)} as close to ${entry.goal.target}${unitSuffix(metrics, entry.goal.metric)} as possible`;
}

/** A lane of the map: "Amazon UK", "Shopify EU", keyed by the store name so the columns read as the approved design's. */
export type Lane = { key: string; label: string; market: Market; channel_location: string; strategised: boolean };
export type MapCell = { effective: string | null; overrides: Array<{ marketplace: string; strategy: string }> };
export type MapTable = { lanes: Lane[]; products: string[]; cells: Record<string, MapCell> };
export const cellKey = (lane: string, product: string) => `${lane}|${product}`;
/** Folds the per-market maps into the design's table: products down, lanes across, marketplace overrides under the lane's cell. */
export function mapTable(maps: StrategyMap[]): MapTable {
  const lanes: Lane[] = []; const products: string[] = []; const cells: Record<string, MapCell> = {};
  const fromRetail = new Set<string>(); // cells whose strategy came from a retail row (units_per_case null)
  for (const map of maps) {
    for (const row of map.rows) {
      const laneKey = row.channel_location;
      if (!lanes.some((l) => l.key === laneKey)) {
        lanes.push({ key: laneKey, label: STORE_LABEL[laneKey] ?? laneKey, market: map.market, channel_location: laneKey, strategised: map.strategised });
      }
      const product = `${row.product} · ${row.variant}`;
      if (!products.includes(product)) products.push(product);
      const key = cellKey(laneKey, product);
      const cell = (cells[key] ??= { effective: null, overrides: [] });
      if (row.grain !== "marketplace") {
        // the lane or store row: what the whole lane runs on. The retail row (units_per_case null) wins over a case
        // SKU of the same product whatever the API's order; a case row fills the cell only while no retail row has.
        const retail = row.units_per_case === null;
        if (!fromRetail.has(key) && (retail || cell.effective === null)) {
          cell.effective = row.effective?.strategy_name ?? null;
          if (retail) fromRetail.add(key);
        }
      } else if (row.assignment && row.inherited === false) {
        cell.overrides.push({ marketplace: row.marketplace_name ?? row.marketplace_id, strategy: row.assignment.strategy_name });
      }
    }
  }
  lanes.sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)); // code-point order: no locale in the fold
  return { lanes, products, cells };
}

// ---- Strategy evaluation (C1 v1.12, companion of precision-pricing c2-strategy-eval): C2 judges the assigned strategy's
// rules on the projection's figures and suggests a price; the browser shows the verdicts and never re-judges (H12).

export type RuleStatus = "met" | "not_met" | "n_a" | "no_data";
export type RuleVerdict = {
  priority: number; rule_id: number; metric: string; label: string; operator: Operator; threshold: string; channel_scope: ChannelScope;
  text: string; status: RuleStatus; value: string | null; reason: string | null; dropped: boolean; dropped_reason: string | null;
  /** C2 v1.21: the currency of a money metric's value (GBP), null for a points metric; absent in a snapshot written before it. */
  currency?: string | null;
};
export type GoalVerdict = { metric: string | null; label: string | null; target: string | null; text: string; value: string | null; status: "at" | "below" | "above" | null; reason: string | null; currency?: string | null };
/** A verdict's figure: money in C2's currency for a money metric (price per serving), else percentage points. */
export const verdictFigure = (value: string, currency?: string | null) => (currency ? money(value, currency) : `${value}%`);
export type SuggestedPrice = {
  value: string | null; currency: string; source: "goal" | "window_low" | "window_high" | null; goal_price: string | null;
  window: { low: string; high: string | null } | null; dropped: number[]; verified: boolean | null; rules_met_at: number[] | null; reason: string | null;
  /** C2 v1.71 (c2-run-propose R10, R11): the rules that read no data at the suggestion, and the price-ending step. Absent before. */
  unverified?: UnverifiedRule[]; ending?: ProposeEnding | null;
};
export type StrategyTone = "pass" | "warn" | "fail";
/** The `strategy` block of a project answer (C2 v1.14 System Contract): the one read that carries the suggestion. */
export type StrategyBlock = {
  assigned: { strategy_id: number; name: string; marketplace_id: string; inherited: boolean } | null;
  not_assigned: string | null; not_strategised: string | null; rules: RuleVerdict[]; met: number; applicable: number;
  tone: StrategyTone | null; summary: string | null; goal: GoalVerdict | null; suggested_price: SuggestedPrice | null; method: string;
  /** C2 v1.71 (R10): the rules that read no data at this price. Absent before. */
  unverified?: UnverifiedRule[];
};
/** The block of each bulk-read row. C2 evaluates these rows `with_suggestion=False`, so `suggested_price` is always null: the type
 * refuses a read of it (tests/fixtures/precision-pricing-eval-read-real.json is this read as C2 sends it). */
export type EvalBlock = Omit<StrategyBlock, "suggested_price"> & { suggested_price: null };
export type EvalRow = {
  xero_sku: string; marketplace_id: string; grain: Grain; product: string; variant: string; channel: string;
  price: { value: string; currency: string; pending: boolean; line_id: number | null } | null; no_price: string | null; strategy: EvalBlock | null;
};
export type StrategyEval = {
  market: Market; basis: Basis; as_of: string; strategised: boolean; not_strategised: string | null; row_count: number; evaluated_count: number;
  rows: EvalRow[]; method: string;
};

/** The evaluation of one market on one basis, on a picked date when `asOf` is set (C2 v1.27 `?as_of=`): the rules are judged at
 * the fees in force on it. The default basis on today asks with no query string, exactly as before. */
export const getStrategyEval = (market: Market, basis: Basis = DEFAULT_BASIS, asOf: string | null = null) => {
  const query = [basis === DEFAULT_BASIS ? null : `basis=${basis}`, asOf === null ? null : `as_of=${asOf}`].filter(Boolean).join("&");
  return call<StrategyEval>(`/strategies/eval/${market}${query ? `?${query}` : ""}`);
};
export const EVAL_KEY = ["pricing", "strategies", "eval"] as const;
/** The order-type views (C1 v1.23, C2 v1.23) are the evaluation at a view's net price, so their key sits under the evaluation's:
 * every write that re-reads the evaluation re-reads them too, and a discount write adds them (`refreshSubscriptions`). */
export const VIEWS_KEY = [...EVAL_KEY, "views"] as const;
/** One read per market, basis and date, kept as long as the grid (five minutes): a save, a remove or a commit invalidates it,
 * because the effective price a row is judged at changes with the open run. A picked date adds two parts to today's key, as the
 * grid's does, so the dated read sits under the same prefix and every write re-reads it too (C1 v1.25). */
export function evalQuery(market: Market, basis: Basis = DEFAULT_BASIS, asOf: string | null = null) {
  const queryKey = asOf === null ? [...EVAL_KEY, market, basis] : [...EVAL_KEY, market, basis, "as_of", asOf];
  return { queryKey, queryFn: () => getStrategyEval(market, basis, asOf), retry: 1, staleTime: GRID_STALE_TIME };
}

export type EvalRead = { eval: StrategyEval | null; failed: boolean };
export type EvalReads = Partial<Record<Market, EvalRead>>;
export type EvalLookup =
  | { kind: "pending" } | { kind: "failed" } | { kind: "none"; eval: StrategyEval } | { kind: "row"; row: EvalRow; eval: StrategyEval };
/** The evaluation of one Price Run row: by SKU and place, as the RRP overlay is looked up. A ship-country row carries its store's. */
export function evalLookup(reads: EvalReads | undefined, market: Market, sku: string, place: string): EvalLookup {
  const read = reads?.[market];
  if (!read || (!read.eval && !read.failed)) return { kind: "pending" };
  if (!read.eval) return { kind: "failed" };
  const row = read.eval.rows.find((r) => r.xero_sku === sku && r.marketplace_id === place);
  return row ? { kind: "row", row, eval: read.eval } : { kind: "none", eval: read.eval };
}

/** The strategy name under a Price Run row (design call P): the live evaluation's assigned name, so an edit on the Strategies
 * tab shows on the next read rather than after the next grid run; the grid's daily copy only while the evaluation is pending,
 * failed or judged no price. None when the evaluation says no strategy runs there. */
export function assignedName(lookup: EvalLookup, gridName: string | null): string | null {
  if (lookup.kind === "pending" || lookup.kind === "failed") return gridName;
  if (lookup.kind === "none") return null;
  return lookup.row.strategy ? (lookup.row.strategy.assigned?.name ?? null) : gridName;
}

/** The chip's border per verdict; a passing chip keeps the ink colour (the gross margin's target tone owns `text-ks-pass` in the box). */
export const TONE_CLASS: Record<StrategyTone, string> = { pass: "border-ks-pass", warn: "border-ks-warn text-ks-warn", fail: "border-ks-danger text-ks-warn" };
export const STATUS_WORD: Record<RuleStatus, string> = { met: "met", not_met: "not met", n_a: "n/a", no_data: "no data" };
export const SOURCE_WORD: Record<NonNullable<SuggestedPrice["source"]>, string> = {
  goal: "where the goal is met", window_low: "the lowest price the rules allow (the goal sits below it)", window_high: "the highest price the rules allow (the goal sits above it)",
};
export const EVAL_COPY = {
  reading: "Rules: reading",
  unread: "Rules not read",
  unreadDetail: "The strategy evaluation could not be read. The rules are on the Strategies tab; try again shortly.",
  noStrategy: "No strategy assigned",
  notApplied: "Not applied",
  noPrice: "No price to judge",
  at: (pending: boolean, price: string) => (pending ? `judged at the saved price ${price}` : `judged at the committed price ${price}`),
  inherited: (marketplace: string, channel: string) => (channel === "shopify" ? "set for the store" : marketplace === "ALL" ? "set for the whole SKU" : "set for this marketplace"),
  rule: (priority: number) => `Rule ${priority}`,
  now: (value: string, currency?: string | null) => `now ${verdictFigure(value, currency)}`,
  goalNow: (value: string, target: string, currency?: string | null) => `now ${verdictFigure(value, currency)}, target ${verdictFigure(target, currency)}`,
  dropped: (reason: string) => `dropped from the window: ${reason}`,
  region: "Strategy at the new price",
  suggested: (price: string) => `Suggested price ${price}`,
  use: (price: string) => `Use ${price}`,
  noSuggestion: (reason: string) => `No suggested price: ${reason}`,
  window: (low: string, high: string | null) => (high === null ? `the rules allow ${low} and above` : `the rules allow ${low} to ${high}`),
  verified: "every rule that can be checked holds at it",
  unverified: "not every rule holds at it: the rules above win",
  inBox: "The suggested price is worked out in the New price box above, at the typed price.",
};

// ---- The rule-verdict snapshot on commit (C1 v1.14, companion of precision-pricing c2-verdict-snapshot, C2 v1.16): the
// verdicts C2 recorded on a Price Log entry when its price was committed. Shown as stored, never re-judged (H12); an entry
// written before the snapshot existed (the legacy import, a threshold) carries none and says so.

/** A rule as the snapshot recorded it: the window belongs to the suggestion and is not taken at commit, so nothing is dropped. */
export type SnapshotRule = Omit<RuleVerdict, "dropped" | "dropped_reason"> & { dropped: null; dropped_reason: null };
/** The stored snapshot (C2 v1.16 System Contract): what it was judged on, then the strategy block without its suggestion. */
export type StrategySnapshot = {
  xero_sku: string; marketplace_id: string; price: { value: string; currency: string }; cogs_basis: Basis; as_of: string;
  not_evaluated: string | null; assigned: StrategyBlock["assigned"]; not_assigned: string | null; not_strategised: string | null;
  rules: SnapshotRule[]; met: number; applicable: number; tone: StrategyTone | null; summary: string | null; goal: GoalVerdict | null;
  method: string; snapshot_method: string;
};

export const SNAPSHOT_COPY = {
  none: "no snapshot recorded",
  goal: { at: "goal at target", below: "goal below target", above: "goal above target" } as Record<NonNullable<GoalVerdict["status"]>, string>,
  goalNotSet: "goal not set",
  goalNotWorkedOut: "goal not worked out",
  notEvaluated: (why: string) => `not evaluated: ${why}`,
  heading: "Latest entry: the rules at commit",
  judged: (price: string, basis: string, date: string) => `judged at the committed price ${price} on the ${basis} basis, ${date}`,
  figure: (value: string, currency?: string | null) => `${verdictFigure(value, currency)} at commit`,
  goalAt: (value: string, target: string, currency?: string | null) => `${verdictFigure(value, currency)} at commit, target ${verdictFigure(target, currency)}`,
};

/** One snapshot as a line: the strategy's name, then C2's summary with its tone, then the goal in words; or the one sentence
 * that says why there is nothing to judge, a not-evaluated line always with its why. Every word is the snapshot's or a copy map's (H12). */
export type SnapshotLine = { name: string | null; summary: string | null; tone: StrategyTone | null; goal: string | null; goalTitle: string | null; note: string | null };
export function snapshotLine(snapshot: StrategySnapshot | null): SnapshotLine {
  const line: SnapshotLine = { name: null, summary: null, tone: null, goal: null, goalTitle: null, note: null };
  if (!snapshot) return { ...line, note: SNAPSHOT_COPY.none };
  if (snapshot.not_strategised) return { ...line, note: snapshot.not_strategised };
  if (!snapshot.assigned) {
    const none = snapshot.not_assigned ?? EVAL_COPY.noStrategy;
    return { ...line, note: snapshot.not_evaluated ? `${none} · ${SNAPSHOT_COPY.notEvaluated(snapshot.not_evaluated)}` : none };
  }
  if (snapshot.not_evaluated) return { ...line, name: snapshot.assigned.name, note: SNAPSHOT_COPY.notEvaluated(snapshot.not_evaluated) };
  const g = snapshot.goal;
  const goal = !g ? null : g.status ? SNAPSHOT_COPY.goal[g.status] : g.metric === null ? SNAPSHOT_COPY.goalNotSet : SNAPSHOT_COPY.goalNotWorkedOut;
  return { ...line, name: snapshot.assigned.name, summary: snapshot.summary, tone: snapshot.tone, goal, goalTitle: g && !g.status ? g.reason : null };
}

// ---- The editor's writes (C1 v1.15, companion of precision-pricing c2-strategy-editing, C2 v1.17). Every write needs the
// module's edit level; C2 refuses a view-only person (403) and the store's refusals come back in the error envelope. The
// browser sends what the person picked or typed (a threshold and a target as decimal strings, a strategy as C2's integer id
// looked up from the library) and never works out a priority: C2 appends, renumbers and reorders.

export const NAME_MAX = 120;
export const NOTE_MAX = 500;

export type StrategyBody = { name: string; goal_metric: string; goal_target: string };
/** A rename and/or a goal edit, by id: only the fields that change; the goal's metric and target travel together. */
export type StrategyEdit = { name?: string; goal_metric?: string; goal_target?: string };
export type RuleBody = { metric: string; operator: Operator; threshold: string; channel_scope?: ChannelScope };
/** C2 v1.17 (review round 2): `expected_strategy_id` is the own strategy the tab saw on that SKU and marketplace (null: none), so a
 * write over a cell changed elsewhere is refused (`changed_elsewhere`) rather than saved over it. */
export type AssignmentBody = { strategy_id: number; marketplace_id: string; note?: string; expected_strategy_id: number | null };
export type ProductNoteKey = { tracking: string; variant: string };
export type LaneNoteKey = { channel_location: string; marketplace_id: string };

/** A rename or goal edit re-versions the strategy: the answer is the new entry, the id it replaced and whether anything was written. */
export type StrategyEditAnswer = StrategyEntry & { previous_id: number | null; unchanged: boolean };
/** A rule appended (no `unchanged`) or replaced by id. */
export type RuleAnswer = StrategyEntry & { rule_id: number; unchanged?: boolean };
export type RuleRetireAnswer = StrategyEntry & { retired: number };
export type OrderAnswer = StrategyEntry & { unchanged: boolean; renumbered: number };
/** A strategy retired. With a replacement (or `none`) C2 v1.17 also says where its cells went: moved to the replacement, left with no
 * strategy, marketplace overrides now reading their lane, rows on retired SKUs cleared; without one the answer is C2 v1.9's. */
export type RetireAnswer = {
  id: number; name: string; retired: number; rules_retired: number; replacement?: StrategyRef | null; reassigned?: number; unassigned?: number;
  follows_lane?: number; retired_cleared?: number;
};
export type TemplateAnswer = Template & { unchanged: boolean };
export type TemplateRetireAnswer = { id: number; retired: number };
export type NoteAnswer = Note | { cleared: true };
/** The map row of the key written, with whether anything changed (a put) or how many rows were retired (a delete). */
export type AssignmentAnswer = MapRow & { unchanged?: boolean; retired?: number };

/** The Select value that clears a lane (`DELETE ...?marketplace_id=ALL`) and the delete route's no-replacement word. */
export const NO_STRATEGY = "none";
/** The Select value that drops a marketplace's own strategy so it follows the lane again. */
export const SAME_AS_LANE = "lane";

const send = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });
/** A note as C2 stores it: trimmed, and blank means clear (sent as null). */
export function cleanNote(raw: string | null): string | null {
  const text = raw?.trim() ?? "";
  return text ? text : null;
}
export const createStrategy = (body: StrategyBody) => call<StrategyEntry>("/strategies", send("POST", body));
export const editStrategy = (id: number, body: StrategyEdit) => call<StrategyEditAnswer>(`/strategies/${id}`, send("PATCH", body));
/** The mutation key of that save (Rename or Edit goal). It re-versions the strategy to a new id, so the card reads it to hold every
 * write that names the old id until the refetch brings the new one. A mutation key: no read shares it, no refresh touches it. */
export const editStrategyKey = (id: number) => ["pricing", "strategies", "edit", id] as const;
/** Without a replacement C2 refuses while the strategy is assigned; with one (an id, or `none`) it moves or clears every cell first. */
export const deleteStrategy = (id: number, replacement?: number | typeof NO_STRATEGY) =>
  call<RetireAnswer>(`/strategies/${id}${replacement === undefined ? "" : `?replacement_id=${replacement}`}`, { method: "DELETE" });
export const addRule = (id: number, body: RuleBody) => call<RuleAnswer>(`/strategies/${id}/rules`, send("POST", body));
export const setRule = (id: number, ruleId: number, body: RuleBody) => call<RuleAnswer>(`/strategies/${id}/rules/by-id/${ruleId}`, send("PUT", body));
export const deleteRule = (id: number, ruleId: number) => call<RuleRetireAnswer>(`/strategies/${id}/rules/by-id/${ruleId}`, { method: "DELETE" });
/** Exactly the active rule ids, in the new order. */
export const reorderRules = (id: number, ruleIds: number[]) => call<OrderAnswer>(`/strategies/${id}/rules/order`, send("PUT", { rule_ids: ruleIds }));
export const saveTemplate = (body: RuleBody) => call<TemplateAnswer>("/strategies/templates", send("POST", body));
export const deleteTemplate = (id: number) => call<TemplateRetireAnswer>(`/strategies/templates/${id}`, { method: "DELETE" });
/** `expected` is the note text the tab saw (null: none): C2 refuses the write (`changed_elsewhere`) when the note changed meanwhile. */
export const setProductNote = (key: ProductNoteKey, text: string | null, expected: string | null) =>
  call<NoteAnswer>("/strategies/notes/product", send("PUT", { ...key, text: cleanNote(text), expected_text: expected }));
export const setLaneNote = (key: LaneNoteKey, text: string | null, expected: string | null) =>
  call<NoteAnswer>("/strategies/notes/lane", send("PUT", { ...key, text: cleanNote(text), expected_text: expected }));
export const putAssignment = (sku: string, body: AssignmentBody) =>
  call<AssignmentAnswer>(`/strategies/assignments/${encodeURIComponent(sku)}`, send("PUT", body));
export const deleteAssignment = (sku: string, marketplaceId: string) =>
  call<AssignmentAnswer>(`/strategies/assignments/${encodeURIComponent(sku)}?marketplace_id=${encodeURIComponent(marketplaceId)}`, { method: "DELETE" });

export const getStrategyCells = (id: number) => call<StrategyCell[]>(`/strategies/${id}/cells`);
/** The delete guard's read: never stale, because it lists what a delete is about to move. Under the library prefix, so every
 * strategy write drops it. */
export function cellsQuery(id: number) {
  return { queryKey: [...LIBRARY_KEY, "cells", id] as const, queryFn: () => getStrategyCells(id), retry: 1, staleTime: 0 };
}

/** After every strategy write, refused or not: the library, the three maps, any delete-guard read and every evaluation read share
 * the `["pricing", "strategies"]` prefix, and the New price box's projection carries the strategy block, so the Price Run's chips
 * and drawer follow the edit on their next render. A refusal refreshes too, because most mean the page is behind the store.
 * Settles once the active reads have refetched (a failed refetch does not throw), so a write can stay pending until the page
 * shows C2's new rule ids and lane: a click in between would send the old ones. */
export function refreshStrategies(client: QueryClient): Promise<unknown> {
  return Promise.all([
    client.invalidateQueries({ queryKey: LIBRARY_KEY }),
    client.invalidateQueries({ queryKey: PROJECTION_KEY }),
  ]);
}

/** The sentence for a refused strategy write: the tab's own where C2's reason needs words a reader can act on (a stale page, a
 * taken name, a template in use), C2's own sentence otherwise (the store's without its `pricing: ` prefix), the access sentence
 * on a 401 or 403, the not-yet sentence when the route is not served, the unreachable one when nothing answered. */
export function strategyRefusal(error: unknown, areaName: string): string {
  const T = STRATEGIES_COPY.refusal;
  if (!(error instanceof PricingError)) return T.unreachable;
  const reason = error.reason();
  const facts: ErrorFacts & { used_by?: StrategyRef[] } = error.facts();
  if (reason === "template_in_use") return STRATEGIES_COPY.library.inUse(STRATEGIES_COPY.library.thisRule, (facts.used_by ?? []).map((s) => s.name));
  if (reason && T.byReason[reason]) return T.byReason[reason];
  if (error.status === 401 || error.status === 403) return T.noEdit(areaName);
  // no C2 reason on a 404 or 405: the route itself is not there (C2 before `deploy/api.md` step 20)
  if ((error.status === 404 || error.status === 405) && !facts.reason) return T.notLive;
  if (facts.message && (error.status === 409 || error.status === 422 || error.status === 404)) return writeRefusal(error, areaName);
  return T.unreachable;
}

// ---- Pure helpers the editor is built from. Nothing here reads a figure as a number: ids are looked up, never parsed.

/** A threshold or goal target as C2 takes it (a decimal string, sign allowed, at most four decimals), or null: a comma reads as
 * the point, a leading plus or typographic minus is accepted, and a negative zero is sent unsigned. A magnitude of C2's
 * `MAX_VALUE` (9999999999, NUMERIC(14,4)) or more is refused here, so the form's own sentence shows rather than C2's
 * "goal_target is out of range", or a store overflow for a large negative. */
export const SIGNED_MAX = "9999999999";
export function cleanSigned(raw: string): string | null {
  const m = /^([+\-−]?)(\d+(?:\.\d{1,4})?)$/.exec(raw.trim().replace(/,/g, "."));
  if (!m) return null;
  // by its digits, never parsed: the whole part without leading zeros against C2's whole-number bound (a fraction cannot reach it)
  const whole = m[2].split(".")[0].replace(/^0+(?=\d)/, "");
  if (whole.length > SIGNED_MAX.length || (whole.length === SIGNED_MAX.length && whole >= SIGNED_MAX)) return null;
  const zero = /^0+(\.0+)?$/.test(m[2]);
  return (m[1] === "-" || m[1] === "−") && !zero ? `-${m[2]}` : m[2];
}

/** The operators a rule may take in the builder (at least, at most, equal: the legacy vocabulary, design call E; "exactly" in
 * words, as C2's sentence says it), plus the rule's own operator when C2 holds another of the six, so editing a rule never
 * changes its operator silently. */
export const RULE_OPERATORS: Operator[] = [">=", "<=", "="];
export function operatorOptions(current?: Operator): Array<{ value: Operator; label: string }> {
  const ops = current && !RULE_OPERATORS.includes(current) ? [...RULE_OPERATORS, current] : RULE_OPERATORS;
  return ops.map((value) => ({ value, label: STRATEGIES_COPY.operator[value] }));
}
export const SCOPES: ChannelScope[] = ["any", "amazon", "shopify"];
export const scopeOptions = () => SCOPES.map((value) => ({ value, label: STRATEGIES_COPY.scope[value] }));

export const LENSES: Lens[] = ["productivity", "customer", "competition"];
export type LensGroup<T> = { lens: string | null; label: string | null; items: T[] };
/** Groups metrics or templates by lens in the drawer's order (productivity, customer, competition), each group in C2's order. A
 * lens C2 adds later follows under its own key; items with no lens (C2 before v1.17) come last, unlabelled when they are all. */
export function byLens<T extends { lens?: string | null }>(items: T[]): Array<LensGroup<T>> {
  const known: Record<string, string> = STRATEGIES_COPY.lens;
  const order: Array<string | null> = [...LENSES];
  for (const item of items) if (item.lens && !order.includes(item.lens)) order.push(item.lens);
  order.push(null);
  const groups = order
    .map((lens) => ({ lens, items: items.filter((i) => (i.lens ?? null) === lens) }))
    .filter((g) => g.items.length > 0);
  return groups.map((g) => ({
    lens: g.lens, items: g.items, label: g.lens === null ? (groups.length === 1 ? null : known.other) : (known[g.lens] ?? g.lens),
  }));
}

/** C2's rule sentence (v1.17 `text`), else the one C1 builds from the same fields. */
export function ruleSentence(rule: Rule, metrics: Metric[]): string {
  return rule.text ?? ruleLabel(rule, metrics);
}
/** Why the evaluator never judges this metric (C2's words), or null. */
export function metricNoData(metrics: Metric[], key: string): string | null {
  return metrics.find((m) => m.metric === key)?.no_data ?? null;
}
export function templateOf(rule: Rule, templates: Template[] | undefined): Template | null {
  return templates?.find((t) => t.id === rule.template_id) ?? null;
}
/** The card's stamp: C2 v1.17's last edit, else the strategy row's setter. */
export function editedStamp(entry: StrategyEntry): { by: string; at: string } {
  return { by: entry.edited_by ?? entry.set_by, at: entry.edited_at ?? entry.set_at };
}
/** The stored priority of a rule in an answer, for the result line (the number the card shows). */
export function rulePriority(entry: StrategyEntry, ruleId: number): number | null {
  return entry.rules.find((r) => r.id === ruleId)?.priority ?? null;
}
/** The rule ids in their new order after moving one a place up (-1) or down (+1); null at either end. */
export function movedOrder(rules: Rule[], ruleId: number, step: -1 | 1): number[] | null {
  const ids = rules.map((r) => r.id);
  const i = ids.indexOf(ruleId);
  const j = i + step;
  if (i < 0 || j < 0 || j >= ids.length) return null;
  [ids[i], ids[j]] = [ids[j], ids[i]];
  return ids;
}

/** A Select value for a strategy id, or the given sentinel for none. */
export const strategyValue = (id: number | null, none: string) => (id === null ? none : String(id));
/** The strategy a Select value names, by lookup in the library (C2 wants its integer id back, never a parsed string). */
export function strategyByValue(strategies: StrategyEntry[], value: string): StrategyEntry | null {
  return strategies.find((s) => String(s.id) === value) ?? null;
}

export type Checked<T> = { ok: true; body: T } | { ok: false; error: string };
const refused = <T>(error: string): Checked<T> => ({ ok: false, error });
/** The New strategy form's body, or the first thing to fix (v26's order: name, taken name, target). */
export type StrategyDraft = { name: string; goal_metric: string; goal_target: string };
export function newStrategyBody(draft: StrategyDraft, strategies: StrategyEntry[]): Checked<StrategyBody> {
  const T = STRATEGIES_COPY.create;
  const name = draft.name.trim();
  if (!name) return refused(T.nameRequired);
  if (strategies.some((s) => s.name === name)) return refused(T.nameTaken);
  if (!draft.goal_metric) return refused(T.goalRequired);
  const target = cleanSigned(draft.goal_target);
  if (target === null) return refused(T.targetRequired);
  return { ok: true, body: { name, goal_metric: draft.goal_metric, goal_target: target } };
}
/** A rename and/or goal edit of one card: only what changed (null when nothing did), the goal's pair together. */
export function editStrategyBody(entry: StrategyEntry, draft: StrategyDraft, strategies: StrategyEntry[]): Checked<StrategyEdit | null> {
  const T = STRATEGIES_COPY.create;
  const name = draft.name.trim();
  if (!name) return refused(T.nameRequired);
  if (strategies.some((s) => s.name === name && s.id !== entry.id)) return refused(T.nameTaken);
  const body: StrategyEdit = {};
  if (name !== entry.name) body.name = name;
  // a strategy with no goal may be renamed alone; a goal once set is edited, never cleared, here
  if (entry.goal !== null || draft.goal_metric || draft.goal_target.trim()) {
    if (!draft.goal_metric) return refused(T.goalRequired);
    const target = cleanSigned(draft.goal_target);
    if (target === null) return refused(T.targetRequired);
    if (draft.goal_metric !== entry.goal?.metric || target !== entry.goal?.target) Object.assign(body, { goal_metric: draft.goal_metric, goal_target: target });
  }
  return { ok: true, body: Object.keys(body).length ? body : null };
}
/** What the rule builder holds: the threshold as typed. */
export type RuleDraft = { metric: string; operator: Operator; threshold: string; channel_scope: ChannelScope };
export const draftOf = (r: Rule | Template): RuleDraft => ({ metric: r.metric, operator: r.operator, threshold: r.threshold, channel_scope: r.channel_scope });
/** The builder's start for Add rule: the first library template (v26), else the first metric with an empty threshold. */
export function defaultDraft(library: Library): RuleDraft {
  const first = library.templates?.[0];
  return first ? draftOf(first) : { metric: library.metrics[0]?.metric ?? "", operator: ">=", threshold: "", channel_scope: "any" };
}
export function ruleBody(draft: RuleDraft): Checked<RuleBody> {
  const T = STRATEGIES_COPY.builder;
  if (!draft.metric) return refused(T.metricRequired);
  const threshold = cleanSigned(draft.threshold);
  if (threshold === null) return refused(T.thresholdRequired);
  return { ok: true, body: { metric: draft.metric, operator: draft.operator, threshold, channel_scope: draft.channel_scope } };
}

/** What Delete strategy sends (design call F): nothing (a plain delete) or `none` for a strategy not assigned on an active SKU;
 * for an assigned one the chosen replacement's id, looked up in the library as it is now, or No strategy. A choice the library no
 * longer holds (renamed or deleted elsewhere while the dialog was open) is refused, never sent as a plain delete. */
export function deleteReplacement(entry: StrategyEntry, others: StrategyEntry[], choice: string): Checked<number | typeof NO_STRATEGY | undefined> {
  const T = STRATEGIES_COPY.remove;
  if (entry.assignment_count === 0) return { ok: true, body: entry.assigned_retired_skus.length ? NO_STRATEGY : undefined };
  if (!choice) return refused(T.chooseFirst);
  if (choice === NO_STRATEGY) return { ok: true, body: NO_STRATEGY };
  const replacement = strategyByValue(others, choice);
  return replacement ? { ok: true, body: replacement.id } : refused(T.chooseAgain);
}

/** The delete guard's line for one cell: "C8 MCT Oil · 500ml · Amazon EU · Amazon.pl", a retired SKU's marked as cleared. */
export function cellLine(cell: StrategyCell): string {
  const place = `${channelLabel(cell)} ${MARKET_LABEL[cell.market]}${cell.marketplace_name ? ` · ${cell.marketplace_name}` : ""}`;
  return `${cell.product} · ${cell.variant} · ${place}${cell.sku_active ? "" : ` (${cell.xero_sku}, ${STRATEGIES_COPY.remove.retiredCell})`}`;
}

// ---- The editable map (C1 v1.15): the fold that drives one dropdown per strategised cell. Unlike `mapTable` it keeps what a
// write needs: the SKU and its lane key (ALL), the own strategy id and assignment note, the marketplaces of an Amazon EU lane
// with their own or inherited strategy, and the product and lane notes; it also tells an unassigned SKU (C2's not-set sentence)
// from a lane with no SKU for the product (no cell at all).

export type LaneNoteSlot = LaneNoteKey & { marketplace_name: string; note: Note | null };
/** One marketplace under an Amazon lane: its own strategy (null means it follows the lane), what runs there and the lane note there. */
export type MarketplaceSlot = LaneNoteSlot & {
  ownId: number | null; ownName: string | null; ownNote: string | null; effectiveName: string | null; inherited: boolean;
};
export type EditCell = {
  sku: string; marketplace_id: string; product: string; variant: string; lane: string; market: Market; channel: Channel;
  strategised: boolean; poolSingle: boolean;
  /** The lane's (or store's) own strategy and assignment note; null with C2's `notSet` sentence when the SKU has none. */
  strategyId: number | null; strategyName: string | null; note: string | null; notSet: string | null;
  /** The lane note of a one-marketplace pool (Amazon UK, Amazon UAE), keyed by that marketplace; null elsewhere. */
  laneNote: LaneNoteSlot | null;
  /** Every marketplace of a multi-marketplace pool; on a one-marketplace pool only a leftover own row, so it can be cleared. */
  marketplaces: MarketplaceSlot[];
};
export type EditLane = Lane & { channel: Channel; poolSingle: boolean; notes: LaneNoteSlot[] };
export type EditProduct = ProductNoteKey & { key: string; note: Note | null };
export type EditableMap = { lanes: EditLane[]; products: EditProduct[]; cells: Record<string, EditCell>; notStrategised: string[] };

/** Folds the per-market maps into the editor's table, keyed as `mapTable` (`cellKey(lane, "product · variant")`). Of several SKUs
 * of one product on one lane the retail SKU (`units_per_case` null) is the cell's, whatever the API's order; a case SKU only when
 * there is no retail one. Lanes sort by label, products keep the API's order. */
export function editableMap(maps: StrategyMap[]): EditableMap {
  const lanes: EditLane[] = []; const products: EditProduct[] = []; const cells: Record<string, EditCell> = {};
  const notStrategised: string[] = [];
  const groups = new Map<string, Map<string, MapRow[]>>(); // cell key -> SKU -> its rows (lane or store row, then marketplaces)
  for (const map of maps) {
    if (!map.strategised && map.not_strategised) notStrategised.push(map.not_strategised);
    for (const row of map.rows) {
      if (!lanes.some((l) => l.key === row.channel_location)) {
        lanes.push({
          key: row.channel_location, label: STORE_LABEL[row.channel_location] ?? row.channel_location, market: map.market,
          channel_location: row.channel_location, strategised: map.strategised, channel: row.channel, poolSingle: false, notes: [],
        });
      }
      const key = `${row.product} · ${row.variant}`;
      const product = products.find((p) => p.key === key);
      if (!product) products.push({ key, tracking: row.product, variant: row.variant, note: row.product_note ?? null });
      else if (!product.note && row.product_note) product.note = row.product_note;
      const bySku = groups.get(cellKey(row.channel_location, key)) ?? new Map<string, MapRow[]>();
      groups.set(cellKey(row.channel_location, key), bySku);
      bySku.set(row.xero_sku, [...(bySku.get(row.xero_sku) ?? []), row]);
    }
  }
  for (const [key, bySku] of groups) {
    const skus = [...bySku.values()];
    const rows = skus.find((r) => r[0].units_per_case === null) ?? skus[0];
    const head = rows.find((r) => r.grain !== "marketplace");
    if (!head) continue; // C2 always serves the lane or store row first; a marketplace row alone has nothing to assign
    const lane = lanes.find((l) => l.key === head.channel_location) as EditLane;
    const places = rows.filter((r) => r.grain === "marketplace");
    const single = head.pool_single ?? (head.channel === "amazon" && places.length === 1);
    const slotKey = (r: MapRow, note: Note | null | undefined): LaneNoteSlot => ({
      channel_location: r.channel_location, marketplace_id: r.marketplace_id, marketplace_name: r.marketplace_name ?? r.marketplace_id, note: note ?? null,
    });
    const slots: MarketplaceSlot[] = places.map((r) => ({
      ...slotKey(r, r.lane_note), ownId: r.assignment?.strategy_id ?? null, ownName: r.assignment?.strategy_name ?? null,
      ownNote: r.assignment?.note ?? null, effectiveName: r.effective?.strategy_name ?? null, inherited: r.inherited !== false,
    }));
    const laneNote = single && places.length > 0 ? slotKey(places[0], head.lane_note ?? places[0].lane_note) : null;
    for (const slot of laneNote ? [laneNote] : single ? [] : slots) {
      const known = lane.notes.find((n) => n.marketplace_id === slot.marketplace_id);
      if (!known) lane.notes.push({ channel_location: slot.channel_location, marketplace_id: slot.marketplace_id, marketplace_name: slot.marketplace_name, note: slot.note });
      else if (!known.note && slot.note) known.note = slot.note;
    }
    lane.poolSingle = single;
    cells[key] = {
      sku: head.xero_sku, marketplace_id: head.marketplace_id, product: head.product, variant: head.variant, lane: head.channel_location,
      market: head.market, channel: head.channel, strategised: lane.strategised, poolSingle: single,
      strategyId: head.assignment?.strategy_id ?? null, strategyName: head.assignment?.strategy_name ?? null,
      note: head.assignment?.note ?? null, notSet: head.assignment ? null : (head.not_set ?? EVAL_COPY.noStrategy),
      laneNote, marketplaces: single ? slots.filter((s) => s.ownId !== null) : slots,
    };
  }
  lanes.sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)); // code-point order, as `mapTable`
  return { lanes, products, cells, notStrategised };
}

/** The write a map dropdown asks for, or null when it changes nothing (or the lane is not strategised). A put keeps the
 * assignment's own note: C2 would otherwise store the note as cleared. Every put names the own strategy the tab saw there. */
export type MapWrite =
  | { kind: "put"; sku: string; body: AssignmentBody; strategy: StrategyEntry }
  | { kind: "delete"; sku: string; marketplace_id: string };
function put(cell: EditCell, marketplaceId: string, strategy: StrategyEntry, note: string | null, expected: number | null): MapWrite {
  return { kind: "put", sku: cell.sku, body: assignmentBody(strategy.id, marketplaceId, note, expected), strategy };
}
export function assignmentBody(strategyId: number, marketplaceId: string, note: string | null, expected: number | null): AssignmentBody {
  const text = cleanNote(note);
  return { strategy_id: strategyId, marketplace_id: marketplaceId, ...(text ? { note: text } : {}), expected_strategy_id: expected };
}
/** The assignment note's write (review round 2): the same strategy and marketplace put again with the new note, or with none to
 * clear it. Null when the cell (or the marketplace) has no own strategy: there is no assignment to carry a note. */
export function noteWrite(cell: EditCell, slot: MarketplaceSlot | null, text: string | null): { sku: string; body: AssignmentBody } | null {
  if (!cell.strategised) return null;
  const own = slot ? slot.ownId : cell.strategyId;
  if (own === null) return null;
  return { sku: cell.sku, body: assignmentBody(own, slot ? slot.marketplace_id : cell.marketplace_id, text, own) };
}
/** The lane's (or store's) dropdown: a strategy, or `NO_STRATEGY` to clear the lane. */
export function cellWrite(cell: EditCell, value: string, strategies: StrategyEntry[]): MapWrite | null {
  if (!cell.strategised) return null;
  if (value === NO_STRATEGY) return cell.strategyId === null ? null : { kind: "delete", sku: cell.sku, marketplace_id: cell.marketplace_id };
  const strategy = strategyByValue(strategies, value);
  if (!strategy || strategy.id === cell.strategyId) return null;
  return put(cell, cell.marketplace_id, strategy, cell.note, cell.strategyId);
}
/** A marketplace's dropdown: a strategy of its own, or `SAME_AS_LANE`. Picking the lane's own strategy also follows the lane, so
 * no marketplace row ever repeats its lane (the rule the one-off collapse applied; C2 v1.17 keeps it on a lane write too, by
 * retiring the overrides the new lane strategy equals). */
export function overrideWrite(cell: EditCell, slot: MarketplaceSlot, value: string, strategies: StrategyEntry[]): MapWrite | null {
  if (!cell.strategised) return null;
  const strategy = value === SAME_AS_LANE ? null : strategyByValue(strategies, value);
  if (value !== SAME_AS_LANE && !strategy) return null;
  if (strategy === null || strategy.id === cell.strategyId) {
    return slot.ownId === null ? null : { kind: "delete", sku: cell.sku, marketplace_id: slot.marketplace_id };
  }
  return strategy.id === slot.ownId ? null : put(cell, slot.marketplace_id, strategy, slot.ownNote, slot.ownId);
}

/** A map write as the cell sends it: the write, the place it names, whether it is the lane's (or store's) dropdown, and the
 * marketplaces that keep a strategy of their own ("Amazon.pl (FM % Isolation PRO)"), read when the lane is cleared. */
export type MapSend = { write: MapWrite; place: string; lane: boolean; kept?: string[] };
export function keptApart(cell: EditCell): string[] {
  return cell.marketplaces.filter((m) => m.ownId !== null).map((m) => `${m.marketplace_name} (${m.ownName ?? ""})`);
}
/** The result line of a map write. Clearing a lane retires only its own row (C2 `f_unset_strategy_assignment`), so a marketplace
 * with its own strategy keeps running it and the line says so rather than "no strategy". */
export function mapDone(product: string, sent: MapSend, answer: { unchanged?: boolean }): string {
  const D = STRATEGIES_COPY.done;
  if (answer.unchanged) return D.unchanged;
  if (sent.write.kind === "put") return D.assigned(product, sent.place, sent.write.strategy.name);
  if (!sent.lane) return D.followsLane(product, sent.place);
  return sent.kept?.length ? D.clearedKeeps(product, sent.place, sent.kept.join(", "), sent.kept.length > 1) : D.cleared(product, sent.place);
}

/** A strategy's colour (C1 v1.35, owner ruling 2026-10-05): a fixed palette of brand tokens given out in the order the library
 * lists the strategies, the seventh strategy taking the first colour again. Nothing stores it, and the name is always shown
 * beside it, so the colour is never the only way to tell two strategies apart. No band token (C1 v1.59, GATE-1086 PR-11):
 * a band colour means health only (CLB-017), so the palette is brand purples and neutrals. */
export const STRATEGY_COLOURS = [
  "var(--ks-purple)", "var(--ks-purple-2)", "var(--ks-purple-3)", "var(--ks-grey-1)", "var(--ks-ink)", "var(--ks-purple-shade)",
] as const;
/** The colour of the strategy with this name, or null when the library is unread or does not list it. */
export function strategyColour(strategies: Array<{ name: string }> | undefined, name: string | null | undefined): string | null {
  if (!strategies || !name) return null;
  const at = strategies.findIndex((s) => s.name === name);
  return at < 0 ? null : STRATEGY_COLOURS[at % STRATEGY_COLOURS.length];
}
